import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { toast } from 'sonner';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { useLocalStorage } from '../../../../hooks/useLocalStorage';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { ILocalNotification } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useEventLogging } from '../../../../models/Logging';
import { PersistentStorageManager } from '../../../../models/Storage';
import {
	invalidateAllMyTemplatesFilter,
	invalidateAllTemplateCategorySocialQueries,
	useMyTemplatesFilter,
	useSocialAllCategoryTemplates,
	useSocialCategories,
	useSocialPostDraftsInfiniteQuery,
	useSocialSearchTemplates,
	useTemplateCategorySocialQuery,
	useTemplateDeleteMutation,
	useTemplateScopeMutation,
} from '../../../../queries';
import { useTemplateKeywords } from '../../../../queries/Templates/useEmailTemplateKeywords';
import { ITemplateCard, KnownCategories } from '../../../../viewmodels/AppViewModels';
import { Button } from '../../../components-v2/Button';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { CampaignReportBanner } from '../../../components/campaigns/CampaignReportBanner';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../components/ConfirmationDialog';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { LocalNotificationObserver } from '../../../components/LocalNotificationObserver';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/Select';
import { OopsNotFoundSocialDraftsGraphic } from '../../../components/svgs/graphics/OopsNotFoundSocialDraftsGraphic';
import { ArrowIcon } from '../../../components/svgs/icons/ArrowIcon';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { SocialCampaignTemplateCard } from '../../../components/templateCards/SocialCampaignTemplateCard';
import { TemplateSharingConfirmation } from '../../../components/templates/TemplateSharingConfirmation';
import { baseStyleSheet, bs } from '../../../styles/styles';
import { CampaignBrowser, useCampaignParams, useEnforceIndustryAndCategory } from '../../CampaignBrowser';
import {
	ContentLibraryContent,
	ContentLibrarySectionHeader,
	ContentLibrarySectionHeaderTitle,
	emailCampaignSortByOptions,
	SearchKeywords,
	SortBySelector,
	templateScopeFilterOptions,
} from '../presentation';
import { useTemplateFolders } from '../hooks/useTemplateFolders';
import { styleSheet } from '../styles';
import { TemplateFolderTabs } from '../TemplateFolderTabs';
import { filterLevDraftTemplates } from '../utils';
import { useSocialMediaCampaignBehavior } from './hooks';

const SOCIAL_TEMPLATES_SORT_PREFERENCE_KEY = 'socialTemplatesSortPreferences';
const SOCIAL_REPORT_BANNER_DISMISSED_KEY = 'socialReportBannerDismissed';

const parseSortValue = (sortValue: string) => {
	if (sortValue === 'creationDate' || sortValue === 'creationDate_desc') {
		return { sortBy: 'creationDate', sort: 'desc' as const };
	}
	if (sortValue === 'creationDate_asc') {
		return { sortBy: 'creationDate', sort: 'asc' as const };
	}
	if (sortValue === 'name_asc') {
		return { sortBy: 'name', sort: 'asc' as const };
	}
	if (sortValue === 'name_desc') {
		return { sortBy: 'name', sort: 'desc' as const };
	}
	// Default fallback
	return { sortBy: 'creationDate', sort: 'desc' as const };
};

const formatSortValue = (sortBy: string, sort: 'asc' | 'desc') => {
	if (sortBy === 'creationDate') {
		return sort === 'asc' ? 'creationDate_asc' : 'creationDate_desc';
	}
	if (sortBy === 'name') {
		return sort === 'asc' ? 'name_asc' : 'name_desc';
	}
	return 'creationDate_desc';
};

const useSocialTemplatesSortPreferences = (userSession: Api.UserSessionContext) => {
	const getUserSortKey = React.useCallback(
		() => `${userSession?.user?.id || 'default'}_${SOCIAL_TEMPLATES_SORT_PREFERENCE_KEY}`,
		[userSession?.user?.id]
	);

	const loadSortPreference = React.useCallback(() => {
		try {
			const stored = PersistentStorageManager.local.get(getUserSortKey());
			const defaultValue = 'creationDate_desc';
			const storedValue = stored ? JSON.parse(stored) : defaultValue;
			// Handle both old format (object) and new format (string)
			if (typeof storedValue === 'object' && storedValue.sortBy) {
				return formatSortValue(storedValue.sortBy, storedValue.sort);
			}
			// Handle legacy 'creationDate' value without direction
			if (storedValue === 'creationDate') {
				return 'creationDate_desc';
			}
			return storedValue || defaultValue;
		} catch {
			return 'creationDate_desc';
		}
	}, [getUserSortKey]);

	const saveSortPreference = React.useCallback(
		(sortValue: string) => {
			PersistentStorageManager.local.set(getUserSortKey(), JSON.stringify(sortValue));
		},
		[getUserSortKey]
	);

	return { loadSortPreference, saveSortPreference };
};

export const SocialMediaCampaignCards = observer(() => {
	const userSession = useUserSession();
	const { category, industry, search, setCampaignBrowserParam } = useCampaignParams();
	const { onViewSocialCampaign, onViewAssociatedCampaign, deleteSocialPostMutation, onSocialMediaDraftClicked } =
		useSocialMediaCampaignBehavior('SocialMediaCampaignCards');
	const categoriesQuery = useSocialCategories({
		industry,
		select: categories => {
			const browserCategories = categories.filter(
				c => c !== 'Archived' && c !== 'Uncategorized' && c !== 'HTML Newsletters' && c !== 'My Templates'
			);
			return [
				KnownCategories.All,
				KnownCategories.MyTemplates,
				KnownCategories.SocialMediaDrafts,
				...browserCategories,
			];
		},
	});
	const myDraftsQuery = useSocialPostDraftsInfiniteQuery({
		enabled: category === KnownCategories.SocialMediaDrafts || category === KnownCategories.All,
		userId: userSession.user.id,
	});
	const searchTemplatesQuery = useSocialSearchTemplates({
		enabled: !!search,
		industry,
		query: search,
	});
	useEnforceIndustryAndCategory({
		setCampaignBrowserParam,
		tabUrlParam: 'social',
	});
	const [draftTemplateToDelete, setDraftTemplateToDelete] = React.useState<Api.ISocialMediaPost | null>(null);
	const [isBannerDismissed, setIsBannerDismissed] = useLocalStorage<boolean>({
		key: SOCIAL_REPORT_BANNER_DISMISSED_KEY,
		initialValue: false,
	});

	const handleDeleteConfirmationClose = (result?: IConfirmationDialogOption<boolean>, cancelled?: boolean) => {
		if (!cancelled && result?.representedObject) {
			deleteSocialPostMutation.mutate({ id: draftTemplateToDelete.id });
		}
		setDraftTemplateToDelete(null);
	};
	const renderLoading = () => {
		return (
			<div className={css(styleSheet.loadingContainer)}>
				<LoadingSpinner type='large' />
			</div>
		);
	};
	const renderSearchTemplates = () => {
		if (!search) {
			return;
		}
		if (searchTemplatesQuery.status !== 'success') {
			return renderLoading();
		}
		return (
			<ContentLibraryContent>
				{searchTemplatesQuery.data.length > 0 ? (
					<div className={css(bs.grid, bs.gap5, styleSheet.socialDraftCards)}>
						{searchTemplatesQuery.data.map(card => {
							return (
								<SocialCampaignTemplateCard
									styles={[bs.wAuto, bs.m0]}
									card={card}
									key={card.id}
									onAssociatedTemplateClicked={() => onViewAssociatedCampaign(card.associatedTemplates[0].id)}
									onPreviewClick={() => onViewSocialCampaign(card.id)}
									industry={industry}
								/>
							);
						})}
						<Waypoint bottomOffset='-200px' onEnter={() => myDraftsQuery.fetchNextPage()} />
					</div>
				) : (
					<div className={css(styleSheet.noCampaigns)}>
						<OopsNotFoundSocialDraftsGraphic />
						<div>We did not find any social media drafts based on your search.</div>
						<div>Please try a different search.</div>
					</div>
				)}
			</ContentLibraryContent>
		);
	};
	const renderMyDrafts = () => {
		if (category !== KnownCategories.SocialMediaDrafts || search) {
			return;
		}
		if (myDraftsQuery.status !== 'success') {
			return renderLoading();
		}
		return (
			<ContentLibraryContent>
				{myDraftsQuery.data?.pages[0].values.length > 0 ? (
					<div className={css(bs.bgGray50)}>
						<ContentLibrarySectionHeader>
							<ContentLibrarySectionHeaderTitle>Social Media Drafts</ContentLibrarySectionHeaderTitle>
							<Button
								variant='link'
								size='sm'
								onClick={() => setCampaignBrowserParam({ category: KnownCategories.All })}
							>
								Browse all posts <ArrowIcon fillColor='currentColor' styles={[bs.mt1]} />
							</Button>
						</ContentLibrarySectionHeader>
						<div className={css(bs.grid, bs.gap5, bs.mt5, styleSheet.socialDraftCards)}>
							{myDraftsQuery.data.pages.map(page => {
								return page.values.map(card => {
									return (
										<SocialCampaignTemplateCard
											styles={[bs.wAuto, bs.m0]}
											card={card}
											key={card.id}
											onAssociatedTemplateClicked={() =>
												onViewSocialCampaign(card?.templateReference?.templateId ?? '')
											}
											onPreviewClick={() => onSocialMediaDraftClicked(card)}
											contextMenu={
												<Dropdown>
													<DropdownEllipsisTrigger />
													<DropdownContent>
														<DropdownItem onClick={() => setDraftTemplateToDelete(card)}>Delete</DropdownItem>
													</DropdownContent>
												</Dropdown>
											}
											industry={industry}
										/>
									);
								});
							})}
							<Waypoint bottomOffset='-200px' onEnter={() => myDraftsQuery.fetchNextPage()} />
						</div>
					</div>
				) : (
					<div className={css(styleSheet.noCampaigns)}>
						<OopsNotFoundSocialDraftsGraphic />
						<div>We did not find any social media drafts based on your search.</div>
						<div>Please try a different search.</div>
					</div>
				)}
			</ContentLibraryContent>
		);
	};
	const handleSearchChange = React.useCallback(
		(value: string) => {
			setCampaignBrowserParam({ search: value });
		},
		[setCampaignBrowserParam]
	);

	const onNotification = (notification: ILocalNotification<string>) => {
		if (notification.topic === Topics.EDIT_SOCIAL_POST || notification.topic === Topics.CREATE_SOCIAL_POST) {
			myDraftsQuery.refetch();
		}
	};

	return (
		<>
			<CampaignBrowser
				category={category}
				categories={categoriesQuery?.data ?? []}
				industry={industry}
				search={search}
				onSearchChange={handleSearchChange}
				onCategoryChange={value => setCampaignBrowserParam({ category: value })}
				onIndustryChange={value => setCampaignBrowserParam({ industry: value })}
			>
				{!isBannerDismissed && (
					<CampaignReportBanner
						styles={[bs.mt2]}
						description={
							<>
								See what you have scheduled to go out vs how past campaigns have performed in the&nbsp;
								<NavLink className={css(baseStyleSheet.brandLink)} to='/reporting/social-media-posts'>
									Social Media Reports
								</NavLink>
								&nbsp;section.
							</>
						}
						onClose={() => setIsBannerDismissed(true)}
					/>
				)}
				{!search && category === KnownCategories.MyTemplates ? <CampaignBrowserSocialMyTemplates /> : null}
				{!search && category === KnownCategories.All ? <CampaignBrowserSocialAllTemplates /> : null}
				{renderSearchTemplates()}
				{renderMyDrafts()}
				{!search &&
				![KnownCategories.All, KnownCategories.MyTemplates, KnownCategories.SocialMediaDrafts].includes(category) ? (
					<CampaignBrowserCategoryTemplates />
				) : null}
			</CampaignBrowser>
			<ConfirmationDialog
				icon={<WarningIcon />}
				options={[
					{
						isDestructive: true,
						representedObject: true,
						title: 'Delete',
					},
					{
						isCancel: true,
						representedObject: false,
						title: 'Cancel',
					},
				]}
				title='Are you sure you want to delete this social media draft'
				modalProps={{
					isOpen: draftTemplateToDelete != null,
					onRequestClose: handleDeleteConfirmationClose,
				}}
			/>
			<LocalNotificationObserver onNotificationReceived={onNotification} topic={Topics.EDIT_SOCIAL_POST} />
			<LocalNotificationObserver onNotificationReceived={onNotification} topic={Topics.CREATE_SOCIAL_POST} />
		</>
	);
});

function CampaignBrowserSocialAllTemplates() {
	const userSession = useUserSession();
	const isCustomerImpersonation = userSession?.isCustomerImpersonation;
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('CampaignBrowserSocialAllTemplates');
	const { industry, setCampaignBrowserParam } = useCampaignParams();
	const { onViewSocialCampaign, onViewAssociatedCampaign, deleteSocialPostMutation, onSocialMediaDraftClicked } =
		useSocialMediaCampaignBehavior('CampaignBrowserSocialAllTemplates');
	const [templateIdToDelete, setTemplateIdToDelete] = React.useState<string | null>(null);
	const [templateToShare, setTemplateToShare] = React.useState<ITemplateCard | null>(null);
	const [draftTemplateToDelete, setDraftTemplateToDelete] = React.useState<Api.ISocialMediaPost | null>(null);
	const deleteTemplateMutation = useTemplateDeleteMutation({
		onSuccess: () => {
			invalidateAllTemplateCategorySocialQueries();
			invalidateAllMyTemplatesFilter();
			toast.success('Social media template deleted');
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('DeleteSocialTemplate-Error', error);
		},
	});

	const templateScopeMutation = useTemplateScopeMutation({
		onSuccess: () => {
			invalidateAllTemplateCategorySocialQueries();
			invalidateAllMyTemplatesFilter();
			toast.success('Template share status changed');
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('DeleteSocialTemplate-Error', error);
		},
	});
	const allCategoryTemplatesQuery = useSocialAllCategoryTemplates({
		industry,
		itemListLimit: 5,
	});
	const myTemplatesQuery = useTemplateCategorySocialQuery({
		categoryName: String(KnownCategories.Me).toLocaleLowerCase(),
		industry,
		pageSize: 8,
	});

	// Filter My Templates for the "All" page section
	const filteredMyTemplatesAllPage = React.useMemo(
		() => filterLevDraftTemplates(myTemplatesQuery.data || [], isCustomerImpersonation),
		[myTemplatesQuery.data, isCustomerImpersonation]
	);
	const myDraftsQuery = useSocialPostDraftsInfiniteQuery({
		userId: userSession.user.id,
		pageSize: 8,
	});

	const handleDeleteDraftConfirmationClose = (result?: IConfirmationDialogOption<boolean>, cancelled?: boolean) => {
		if (!cancelled && result?.representedObject) {
			deleteSocialPostMutation.mutate({ id: draftTemplateToDelete.id });
		}
		setDraftTemplateToDelete(null);
	};
	const handleDeleteTemplateConfirmationClose = (result?: IConfirmationDialogOption<boolean>, cancelled?: boolean) => {
		if (!cancelled && result?.representedObject) {
			deleteTemplateMutation.mutate({ id: templateIdToDelete });
		}
		setTemplateIdToDelete(null);
	};
	const handleShareConfirmationClose = (result?: IConfirmationDialogOption<boolean>, cancelled?: boolean) => {
		if (!cancelled && result?.representedObject) {
			templateScopeMutation.mutate({
				scope: templateToShare.scope === Api.TemplateScope.Account ? Api.TemplateScope.User : Api.TemplateScope.Account,
				id: templateToShare.id,
			});
		}
		setTemplateToShare(null);
	};
	if (!allCategoryTemplatesQuery.isSuccess) {
		return (
			<div className={css(bs.flex, bs.itemsCenter, bs.justifyCenter, bs.pt5)}>
				<LoadingSpinner type='large' />
			</div>
		);
	}
	const categories = Object.keys(allCategoryTemplatesQuery.data).filter(
		c => ![KnownCategories.Featured, KnownCategories.SocialMediaDrafts, KnownCategories.Me].includes(c)
	) as KnownCategories[];
	return (
		<ContentLibraryContent>
			{allCategoryTemplatesQuery.data.Featured?.length ? (
				<div>
					<ContentLibrarySectionHeader>
						<ContentLibrarySectionHeaderTitle>Featured Posts</ContentLibrarySectionHeaderTitle>
						<Button
							variant='link'
							size='sm'
							onClick={() => setCampaignBrowserParam({ category: KnownCategories.Featured })}
						>
							Browse all featured posts <ArrowIcon fillColor='currentColor' styles={[bs.mt1]} />
						</Button>
					</ContentLibrarySectionHeader>
					<div className={css(bs.grid, bs.mt5, bs.gap5, styleSheet.socialDraftCards)}>
						{allCategoryTemplatesQuery.data.Featured.map(card => {
							return (
								<SocialCampaignTemplateCard
									styles={[bs.wAuto, bs.m0]}
									card={card}
									key={card.id}
									isFeatured={true}
									onAssociatedTemplateClicked={() => onViewAssociatedCampaign(card.associatedTemplates[0].id)}
									onPreviewClick={() => onViewSocialCampaign(card.id)}
								/>
							);
						})}
					</div>
				</div>
			) : null}

			{filteredMyTemplatesAllPage.length > 0 ? (
				<div>
					<ContentLibrarySectionHeader>
						<ContentLibrarySectionHeaderTitle>My Templates</ContentLibrarySectionHeaderTitle>
						<Button
							variant='link'
							size='sm'
							onClick={() => setCampaignBrowserParam({ category: KnownCategories.MyTemplates })}
						>
							Browse my templates <ArrowIcon fillColor='currentColor' styles={[bs.mt1]} />
						</Button>
					</ContentLibrarySectionHeader>
					<div className={css(bs.grid, bs.mt5, bs.gap5, styleSheet.socialDraftCards)}>
						{filteredMyTemplatesAllPage.map(template => {
							return (
								<SocialCampaignTemplateCard
									styles={[bs.wAuto, bs.m0]}
									card={template}
									key={template.id}
									onAssociatedTemplateClicked={() => onViewAssociatedCampaign(template.associatedTemplates[0].id)}
									onPreviewClick={() => onViewSocialCampaign(template.id)}
									industry={industry}
									invalidateTemplates={invalidateAllTemplateCategorySocialQueries}
									contextMenu={
										template.creator?.id === userSession.user.id || Api.isAdmin(userSession.user) ? (
											<Dropdown>
												<DropdownEllipsisTrigger />
												<DropdownContent>
													<DropdownItem onClick={() => setTemplateIdToDelete(template.id)}>Delete</DropdownItem>
													<DropdownItem onClick={() => setTemplateToShare(template)}>
														{template.scope === Api.TemplateScope.Account ? 'Unshare' : 'Share'}
													</DropdownItem>
												</DropdownContent>
											</Dropdown>
										) : null
									}
								/>
							);
						})}
					</div>
				</div>
			) : null}
			{myDraftsQuery.data?.pages?.[0]?.values?.length ? (
				<div>
					<ContentLibrarySectionHeader>
						<ContentLibrarySectionHeaderTitle>Social Media Drafts</ContentLibrarySectionHeaderTitle>
						<Button
							variant='link'
							size='sm'
							onClick={() =>
								setCampaignBrowserParam({
									category: KnownCategories.SocialMediaDrafts,
								})
							}
						>
							Browse social media drafts <ArrowIcon fillColor='currentColor' styles={[bs.mt1]} />
						</Button>
					</ContentLibrarySectionHeader>
					<div className={css(bs.grid, bs.mt5, bs.gap5, styleSheet.socialDraftCards)}>
						{myDraftsQuery.data.pages.map(page => {
							return page.values.map(card => {
								return (
									<SocialCampaignTemplateCard
										styles={[bs.wAuto, bs.m0]}
										card={card}
										key={card.id}
										onAssociatedTemplateClicked={() => onViewSocialCampaign(card?.templateReference?.templateId ?? '')}
										onPreviewClick={() => onSocialMediaDraftClicked(card)}
										contextMenu={
											<Dropdown>
												<DropdownEllipsisTrigger />
												<DropdownContent>
													<DropdownItem onClick={() => setDraftTemplateToDelete(card)}>Delete</DropdownItem>
												</DropdownContent>
											</Dropdown>
										}
									/>
								);
							});
						})}
					</div>
				</div>
			) : null}
			{categories.map(knownCategory => {
				return (
					<div key={knownCategory}>
						<ContentLibrarySectionHeader>
							<ContentLibrarySectionHeaderTitle>{knownCategory}</ContentLibrarySectionHeaderTitle>
							<Button variant='link' size='sm' onClick={() => setCampaignBrowserParam({ category: knownCategory })}>
								Browse all {knownCategory} posts <ArrowIcon fillColor='currentColor' styles={[bs.mt1]} />
							</Button>
						</ContentLibrarySectionHeader>
						<div className={css(bs.grid, bs.mt5, bs.gap5, styleSheet.socialDraftCards)}>
							{allCategoryTemplatesQuery.data[knownCategory].map(card => {
								return (
									<SocialCampaignTemplateCard
										styles={[bs.wAuto, bs.m0]}
										card={card}
										key={card.id}
										onAssociatedTemplateClicked={() => onViewAssociatedCampaign(card.associatedTemplates[0].id)}
										onPreviewClick={() => onViewSocialCampaign(card.id)}
									/>
								);
							})}
						</div>
					</div>
				);
			})}
			<ConfirmationDialog
				icon={<WarningIcon />}
				options={[
					{
						isDestructive: true,
						representedObject: true,
						title: 'Delete',
					},
					{
						isCancel: true,
						representedObject: false,
						title: 'Cancel',
					},
				]}
				title='Are you sure you want to delete this social media draft'
				modalProps={{
					isOpen: draftTemplateToDelete != null,
					onRequestClose: handleDeleteDraftConfirmationClose,
				}}
			/>
			<ConfirmationDialog
				icon={<WarningIcon />}
				options={[
					{
						isDestructive: true,
						representedObject: true,
						title: 'Delete',
					},
					{
						isCancel: true,
						representedObject: false,
						title: 'Cancel',
					},
				]}
				title='Are you sure you want to delete this social media template'
				modalProps={{
					isOpen: templateIdToDelete != null,
					onRequestClose: handleDeleteTemplateConfirmationClose,
				}}
			/>
			<TemplateSharingConfirmation
				onRequestClose={handleShareConfirmationClose}
				show={templateToShare != null}
				template={templateToShare ?? undefined}
			/>
		</ContentLibraryContent>
	);
}

function CampaignBrowserSocialMyTemplates() {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('CampaignBrowserSocialMyTemplates');
	const isCustomerImpersonation = userSession?.isCustomerImpersonation;
	const { industry, setCampaignBrowserParam, sortBy } = useCampaignParams();

	const { loadSortPreference, saveSortPreference } = useSocialTemplatesSortPreferences(userSession);

	// Load sort preferences or use defaults
	const savedSortPreference = React.useMemo(() => loadSortPreference(), [loadSortPreference]);

	// Determine the effective sort value: URL param > localStorage > default
	const effectiveSortValue = sortBy || savedSortPreference;
	const parsedSort = parseSortValue(effectiveSortValue);
	const effectiveSortBy = parsedSort.sortBy;
	const effectiveSort = parsedSort.sort;

	const [scopeFilter, setScopeFilter] = React.useState<Api.TemplateScopeFilter>(Api.TemplateScopeFilter.All);
	const [keywordsSearchValue, setKeywordsSearchValue] = React.useState<string>('');
	const debouncedKeywordsSearchValue = useDebounceValue(keywordsSearchValue, 500);
	const { onViewAssociatedCampaign, onViewSocialCampaign } = useSocialMediaCampaignBehavior(
		'CampaignBrowserSocialMyTemplates'
	);
	const [templateIdToDelete, setTemplateIdToDelete] = React.useState<string | null>(null);
	const [templateToShare, setTemplateToShare] = React.useState<ITemplateCard | null>(null);
	const deleteTemplateMutation = useTemplateDeleteMutation({
		onSuccess: () => {
			invalidateAllTemplateCategorySocialQueries();
			invalidateAllMyTemplatesFilter();
			toast.success('Social media template deleted');
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('DeleteSocialTemplate-Error', error);
		},
	});
	const templateScopeMutation = useTemplateScopeMutation({
		onSuccess: () => {
			invalidateAllTemplateCategorySocialQueries();
			invalidateAllMyTemplatesFilter();
			toast.success('Template share status changed');
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('DeleteSocialTemplate-Error', error);
		},
	});
	const myTemplatesFilter = useMyTemplatesFilter({
		enabled: true,
		industry,
		term: debouncedKeywordsSearchValue,
		scopeFilter,
		sortBy: effectiveSortBy,
		sort: effectiveSort,
		templateType: Api.TemplateType.SocialMediaPost,
	});

	// Memoize filtered templates at component level
	const filteredMyTemplates = React.useMemo(
		() => filterLevDraftTemplates(myTemplatesFilter.data || [], isCustomerImpersonation),
		[myTemplatesFilter.data, isCustomerImpersonation]
	);

	// Fetch all keywords for folder extraction
	const keywordsQuery = useTemplateKeywords({
		industry,
		enabled: !debouncedKeywordsSearchValue,
	});

	// Folder filtering for My Templates
	const {
		folders,
		selectedFolder,
		setSelectedFolder,
		filteredTemplates: folderFilteredTemplates,
	} = useTemplateFolders({
		templates: filteredMyTemplates,
		allKeywords: keywordsQuery.data ?? [],
	});

	const handleDeleteConfirmationClose = (result?: IConfirmationDialogOption<boolean>, cancelled?: boolean) => {
		if (!cancelled && result?.representedObject) {
			deleteTemplateMutation.mutate({ id: templateIdToDelete });
		}
		setTemplateIdToDelete(null);
	};
	const handleShareConfirmationClose = (result?: IConfirmationDialogOption<boolean>, cancelled?: boolean) => {
		if (!cancelled && result?.representedObject) {
			templateScopeMutation.mutate({
				scope: templateToShare.scope === Api.TemplateScope.Account ? Api.TemplateScope.User : Api.TemplateScope.Account,
				id: templateToShare.id,
			});
		}
		setTemplateToShare(null);
	};

	if (myTemplatesFilter.status !== 'success') {
		return (
			<div className={css(bs.flex, bs.itemsCenter, bs.justifyCenter, bs.pt5)}>
				<LoadingSpinner type='large' />
			</div>
		);
	}

	const selectedSortByCampaignParam =
		emailCampaignSortByOptions.find(i => i.value === effectiveSortValue)?.value || emailCampaignSortByOptions[0].value;
	const handleSortBy = (value: string) => {
		// Save the preference to localStorage
		saveSortPreference(value);
		// Update the URL parameter
		setCampaignBrowserParam({ sortBy: value });
	};
	const handleSearchKeywordsInputChange = (searchValue: string) => {
		setKeywordsSearchValue(searchValue);
	};

	const handleScopeFilterChange = (value: string) => {
		setScopeFilter(value as Api.TemplateScopeFilter);
	};
	const renderNotFound = () => {
		return (
			<div className={css(styleSheet.noCampaigns)}>
				<OopsNotFoundSocialDraftsGraphic />
				<div>We did not find any social media drafts based on your search.</div>
				<div>Please try a different search.</div>
			</div>
		);
	};
	return (
		<ContentLibraryContent>
			<div className={css(bs.flex, bs.flexCol, bs.gap2)}>
				<div className={css(styleSheet.cardSectionTitle)}>
					<h2 className={css(styleSheet.categoryTitle)}>My Templates</h2>
					<div className={css(styleSheet.filtersContainer)}>
						<Select items={templateScopeFilterOptions} value={scopeFilter} onValueChange={handleScopeFilterChange}>
							<SelectTrigger styles={[styleSheet.templateScopeSelect]}>
								<SelectValue placeholder='Filter templates...' />
							</SelectTrigger>
							<SelectContent>
								{templateScopeFilterOptions.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<SortBySelector onChange={handleSortBy} selectedValue={selectedSortByCampaignParam} />
					<SearchKeywords onChange={handleSearchKeywordsInputChange} value={keywordsSearchValue} />
					<Button variant='link' size='sm' onClick={() => setCampaignBrowserParam({ category: KnownCategories.All })}>
						Browse all posts <ArrowIcon fillColor='currentColor' styles={[bs.mt1]} />
					</Button>
				</div>
				{/* Folder tabs - hidden during keyword search */}
				{!debouncedKeywordsSearchValue && folders.length > 0 && (
					<TemplateFolderTabs folders={folders} selectedFolder={selectedFolder} onFolderSelect={setSelectedFolder} />
				)}
				{folderFilteredTemplates.length > 0 ? (
					<div className={css(bs.grid, bs.mt5, bs.gap5, styleSheet.socialDraftCards)}>
						{folderFilteredTemplates.map(template => {
							return (
								<SocialCampaignTemplateCard
									styles={[bs.wAuto, bs.m0]}
									card={template}
									key={template.id}
									onAssociatedTemplateClicked={() => onViewAssociatedCampaign(template.associatedTemplates[0].id)}
									onPreviewClick={() => onViewSocialCampaign(template.id)}
									industry={industry}
									invalidateTemplates={() => {
										invalidateAllTemplateCategorySocialQueries();
										invalidateAllMyTemplatesFilter();
									}}
									contextMenu={
										template.creator?.id === userSession.user.id || Api.isAdmin(userSession.user) ? (
											<Dropdown>
												<DropdownEllipsisTrigger />
												<DropdownContent>
													<DropdownItem onClick={() => setTemplateIdToDelete(template.id)}>Delete</DropdownItem>
													<DropdownItem onClick={() => setTemplateToShare(template)}>
														{template.scope === Api.TemplateScope.Account ? 'Unshare' : 'Share'}
													</DropdownItem>
												</DropdownContent>
											</Dropdown>
										) : null
									}
								/>
							);
						})}
					</div>
				) : myTemplatesFilter.isLoading ? (
					<div className={css(styleSheet.loadingContainer)}>
						<LoadingSpinner type='large' />
					</div>
				) : (
					renderNotFound()
				)}
				<ConfirmationDialog
					icon={<WarningIcon />}
					options={[
						{
							isDestructive: true,
							representedObject: true,
							title: 'Delete',
						},
						{
							isCancel: true,
							representedObject: false,
							title: 'Cancel',
						},
					]}
					title='Are you sure you want to delete this social media template'
					modalProps={{
						isOpen: templateIdToDelete != null,
						onRequestClose: handleDeleteConfirmationClose,
					}}
				/>
				<TemplateSharingConfirmation
					onRequestClose={handleShareConfirmationClose}
					show={templateToShare != null}
					template={templateToShare ?? undefined}
				/>
			</div>
		</ContentLibraryContent>
	);
}

function CampaignBrowserCategoryTemplates() {
	const { onViewAssociatedCampaign, onViewSocialCampaign } = useSocialMediaCampaignBehavior(
		'CampaignBrowserSocialMyTemplates'
	);
	const { industry, category, setCampaignBrowserParam } = useCampaignParams();
	const categoryTemplatesQuery = useTemplateCategorySocialQuery({
		categoryName: category,
		enabled: industry != null && category != null,
		industry,
		retry: (_, error) => error.systemCode !== 404,
	});
	React.useEffect(() => {
		if (categoryTemplatesQuery.error && categoryTemplatesQuery.error.systemCode === 404) {
			setCampaignBrowserParam({ category: KnownCategories.All });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [categoryTemplatesQuery.error]);

	if (!categoryTemplatesQuery.isSuccess) {
		return (
			<div className={css(bs.flex, bs.itemsCenter, bs.justifyCenter, bs.pt5)}>
				<LoadingSpinner type='large' />
			</div>
		);
	}
	return (
		<ContentLibraryContent>
			<div className={css(bs.flex, bs.flexCol)}>
				<ContentLibrarySectionHeader>
					<ContentLibrarySectionHeaderTitle>{category}</ContentLibrarySectionHeaderTitle>
					<Button variant='link' size='sm' onClick={() => setCampaignBrowserParam({ category: KnownCategories.All })}>
						Browse all posts <ArrowIcon fillColor='currentColor' styles={[bs.mt1]} />
					</Button>
				</ContentLibrarySectionHeader>
				<div className={css(bs.grid, bs.mt5, bs.gap5, styleSheet.socialDraftCards)}>
					{categoryTemplatesQuery.data.map(template => {
						return (
							<SocialCampaignTemplateCard
								styles={[bs.wAuto, bs.m0]}
								card={template}
								key={template.id}
								onAssociatedTemplateClicked={() => onViewAssociatedCampaign(template.associatedTemplates[0].id)}
								onPreviewClick={() => onViewSocialCampaign(template.id)}
								industry={industry}
							/>
						);
					})}
				</div>
			</div>
		</ContentLibraryContent>
	);
}
